/**
 * Context menu for marking text as sensitive data
 */

import React, { useEffect, useRef } from 'react';

interface SensitiveDataContextMenuProps {
  x: number;
  y: number;
  selectedText: string;
  onMarkAsSensitive: (dataType: string) => void;
  onClose: () => void;
}

const SENSITIVE_DATA_TYPES = [
  { id: 'email', label: 'Email' },
  { id: 'iban', label: 'IBAN' },
  { id: 'phone', label: 'Phone Number' },
  { id: 'name', label: 'Name' },
  { id: 'address', label: 'Address' },
  { id: 'credit_card', label: 'Credit Card' },
  { id: 'tax_id', label: 'Tax ID' },
  { id: 'postal_code', label: 'Postal Code' },
  { id: 'other', label: 'Other' },
];

export const SensitiveDataContextMenu: React.FC<SensitiveDataContextMenuProps> = ({
  x,
  y,
  selectedText,
  onMarkAsSensitive,
  onClose,
}) => {
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [onClose]);

  return (
    <div
      ref={menuRef}
      className="fixed z-50 bg-white border border-neutral-200 rounded-lg shadow-lg py-1 min-w-[180px]"
      style={{ left: `${x}px`, top: `${y}px` }}
    >
      <div className="px-3 py-2 text-xs font-semibold text-neutral-500 border-b border-neutral-100">
        Mark as Sensitive Data
      </div>
      <div className="max-h-64 overflow-y-auto">
        {SENSITIVE_DATA_TYPES.map((type) => (
          <button
            key={type.id}
            type="button"
            onClick={() => {
              onMarkAsSensitive(type.id);
              onClose();
            }}
            className="w-full text-left px-3 py-2 text-sm text-neutral-700 hover:bg-blue-50 hover:text-blue-700 transition-colors"
          >
            {type.label}
          </button>
        ))}
      </div>
    </div>
  );
};

